import { AlertCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { InlineIssue, inputClass, issueBorder } from './shared';
import type { FieldIssue } from '../../types';

// Per-variant "Offer-led" toggle plus its conditional "Offer End Date" picker.
//
// Shared by the Social flow (PreviewAndAdDetails) and the Search flow
// (SearchFormRenderer), which is why the error state is expressed as a
// FieldIssue: both flows already route backend validation into `fieldIssues`
// under the `offer_end_date` key, and both render errors with InlineIssue.
//
// No `readOnly`/`disabled` prop: every call site sits inside a
// `<fieldset disabled={readOnly}>`, which also covers locked Search versions.

export const OFFER_END_DATE_FIELD_KEY = 'offer_end_date';

const OFFER_END_DATE_REQUIRED = 'Offer End Date is required';
const OFFER_END_DATE_IN_PAST = 'Offer End Date cannot be in the past';

/** Today as `YYYY-MM-DD` in local time — matches what `<input type="date">` emits. */
function todayISO(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Client-side mirror of the backend `_offer_rules` (required_if + not_in_past).
 * Exists so the error appears the moment the toggle flips rather than after a
 * save-and-validate round trip; the backend remains the authority that blocks
 * approval. Returns null when there is nothing wrong.
 */
export function getOfferIssue(isOfferLed: boolean, offerEndDate: string): string | null {
  if (!isOfferLed) return null;
  if (!offerEndDate.trim()) return OFFER_END_DATE_REQUIRED;
  if (offerEndDate < todayISO()) return OFFER_END_DATE_IN_PAST;
  return null;
}

export function OfferLedSection({
  isOfferLed,
  offerEndDate,
  onOfferLedChange,
  onOfferEndDateChange,
  fieldIssues,
  showErrors,
  showWarnings,
}: {
  isOfferLed: boolean;
  offerEndDate: string;
  onOfferLedChange: (value: boolean) => void;
  onOfferEndDateChange: (value: string) => void;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
}) {
  // The client-side message is shown as soon as the toggle flips — it is direct
  // feedback on the user's own action, so it is not gated behind `showErrors`
  // (which only turns on after a validation round trip). The backend issue stays
  // gated like every other field, and any message the client already shows is
  // filtered out of it so nothing appears twice.
  const clientMessage = getOfferIssue(isOfferLed, offerEndDate);
  const backendIssue = fieldIssues[OFFER_END_DATE_FIELD_KEY];
  const backendErrors = (backendIssue?.errors ?? []).filter((m) => m !== clientMessage);
  const issue: FieldIssue | undefined = backendIssue
    ? { ...backendIssue, errors: backendErrors }
    : undefined;

  // Red border for either source, so the field looks the same however the error
  // was discovered.
  const borderClass = clientMessage
    ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
    : issueBorder(issue, showErrors, showWarnings);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Offer-led
          </p>
          <p className="mt-0.5 text-xs text-slate-400">
            Turn on if this ad promotes an offer with a fixed end date
          </p>
        </div>
        <Switch checked={isOfferLed} onCheckedChange={onOfferLedChange} aria-label="Offer-led" />
      </div>

      {isOfferLed && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            Offer End Date
          </p>
          <input
            type="date"
            value={offerEndDate}
            min={todayISO()}
            onChange={(e) => onOfferEndDateChange(e.target.value)}
            aria-label="Offer End Date"
            className={`${inputClass} ${borderClass} ${
              offerEndDate ? 'text-slate-900' : 'text-slate-400'
            }`}
          />
          {clientMessage && (
            <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
              {clientMessage}
            </p>
          )}
          <InlineIssue issue={issue} showErrors={showErrors} showWarnings={showWarnings} />
        </div>
      )}
    </div>
  );
}
